import { useState, useRef, useCallback, useEffect } from 'react';
import { getIdToken } from '@helios/auth-firebase-web';

// ─── Types ───

/** Matches VttMode from @helios/voice-to-text-core — duplicated here to keep UI free of backend deps. */
type VttMode = 'transcription' | 'translation';

export type VttStatus = 'idle' | 'recording' | 'transcribing' | 'error';

interface VttNormalizedResult {
  text: string;
  mode: VttMode;
  sourceLanguage?: string;
  targetLanguage?: string;
  provider: string;
}

export interface UseVoiceToTextReturn {
  status: VttStatus;
  error: string | null;
  /** Seconds elapsed since recording started (ticks every second). */
  elapsed: number;
  /** Maximum recording duration in seconds. */
  maxDuration: number;
  /** Object URL of the last recording, for playback debugging. Null until first recording completes. */
  lastRecordingUrl: string | null;
  /** Start recording from the mic. */
  start: () => void;
  /** Stop recording and send audio to backend. Returns the transcript text on success. */
  stop: () => Promise<string | null>;
  /** Play back the last recording in the browser. */
  playLastRecording: () => void;
  /** Reset error state back to idle. */
  reset: () => void;
}

// ─── Config ───

const VTT_URL = import.meta.env.VITE_VOICE_TO_TEXT_URL;
const MAX_DURATION_S = 120; // 2 minutes

// ─── Hook ───

export function useVoiceToText(
  mode: VttMode = 'transcription',
  /** Called whenever a transcription completes — handles both manual stop and auto-stop. */
  onResult?: (text: string) => void,
): UseVoiceToTextReturn {
  const [status, setStatus] = useState<VttStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const [lastRecordingUrl, setLastRecordingUrl] = useState<string | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const autoStopRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const stopRef = useRef<() => Promise<string | null>>(null!);
  const onResultRef = useRef(onResult);
  onResultRef.current = onResult;

  const cleanupTimers = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (autoStopRef.current) {
      clearTimeout(autoStopRef.current);
      autoStopRef.current = null;
    }
    setElapsed(0);
  }, []);

  const cleanup = useCallback(() => {
    cleanupTimers();
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    mediaRecorderRef.current = null;
    chunksRef.current = [];
  }, [cleanupTimers]);

  const start = useCallback(() => {
    if (status === 'recording') return;

    setError(null);
    setStatus('recording');
    setElapsed(0);
    chunksRef.current = [];

    navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then((stream) => {
        streamRef.current = stream;
        const recorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
        mediaRecorderRef.current = recorder;

        recorder.ondataavailable = (e) => {
          if (e.data.size > 0) chunksRef.current.push(e.data);
        };

        recorder.start();

        timerRef.current = setInterval(() => {
          setElapsed((prev) => prev + 1);
        }, 1000);

        autoStopRef.current = setTimeout(() => {
          stopRef.current();
        }, MAX_DURATION_S * 1000);
      })
      .catch((err) => {
        console.error('[voice-to-text] Mic access denied:', err);
        setError('Microphone access denied');
        setStatus('error');
        cleanup();
      });
  }, [status, cleanup]);

  const stop = useCallback(async (): Promise<string | null> => {
    cleanupTimers();
    const recorder = mediaRecorderRef.current;
    if (!recorder || recorder.state !== 'recording') {
      cleanup();
      setStatus('idle');
      return null;
    }

    const audioBlob = await new Promise<Blob>((resolve) => {
      recorder.onstop = () => {
        resolve(new Blob(chunksRef.current, { type: 'audio/webm' }));
      };
      recorder.stop();
    });

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }

    // Store blob URL for playback debugging
    if (lastRecordingUrl) URL.revokeObjectURL(lastRecordingUrl);
    const blobUrl = URL.createObjectURL(audioBlob);
    setLastRecordingUrl(blobUrl);

    console.log('[voice-to-text] Recording stopped:', {
      blobSize: audioBlob.size,
      blobUrl,
    });

    if (audioBlob.size === 0) {
      setStatus('idle');
      cleanup();
      return null;
    }

    setStatus('transcribing');

    try {
      if (!VTT_URL) throw new Error('Voice-to-text endpoint not configured');

      const base64 = await blobToBase64(audioBlob);
      console.log('[voice-to-text] Sending audio:', {
        blobSize: audioBlob.size,
        base64Length: base64.length,
      });

      const token = await getIdToken();

      const res = await fetch(VTT_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          audio: base64,
          mimeType: 'audio/webm',
          mode,
        }),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({ error: res.statusText }));
        throw new Error(body.error || `API error ${res.status}`);
      }

      const result: VttNormalizedResult = await res.json();

      setStatus('idle');
      cleanup();
      onResultRef.current?.(result.text);
      return result.text;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Transcription failed';
      console.error('[voice-to-text] Error:', err);
      setError(msg);
      setStatus('error');
      cleanup();
      return null;
    }
  }, [mode, cleanup, cleanupTimers, lastRecordingUrl]);

  stopRef.current = stop;

  useEffect(() => cleanup, [cleanup]);

  const playLastRecording = useCallback(() => {
    if (!lastRecordingUrl) return;
    const audio = new Audio(lastRecordingUrl);
    audio.play().catch((err) => console.error('[voice-to-text] Playback failed:', err));
  }, [lastRecordingUrl]);

  const reset = useCallback(() => {
    setError(null);
    setStatus('idle');
    cleanup();
  }, [cleanup]);

  return {
    status,
    error,
    elapsed,
    maxDuration: MAX_DURATION_S,
    lastRecordingUrl,
    start,
    stop,
    playLastRecording,
    reset,
  };
}

// ─── Helpers ───

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      resolve(dataUrl.split(',')[1]);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}
